/**
 * CSAI marker parser (validation tool — not a production code path).
 *
 * Scans an HLS *media* playlist for the SCTE-35 / ad-break signalling tags the
 * Circa edge already emits (verified in `convex/lib/scte35.ts` and the O&O
 * health-check tests): `#EXT-X-CUE-OUT[:dur]`, `#EXT-X-CUE-IN`, and the binary
 * `#EXT-OATCLS-SCTE35` / `#EXT-X-SCTE35` payloads. Each cue is tagged with the
 * absolute segment index it precedes (media-sequence + segments-before-it) so a
 * sliding live playlist can be de-duplicated across polls by a stable key.
 *
 * Pure function. No player, no network — the poller in CsaiTest feeds it text.
 */

export type CueKind = 'CUE-OUT' | 'CUE-IN' | 'SCTE35';

export interface Cue {
  kind: CueKind;
  /** Ad-break duration in seconds when the tag carries one (CUE-OUT only). */
  duration?: number;
  /** Absolute HLS segment index this cue precedes (media-sequence relative). */
  segmentIndex: number;
  /** The raw manifest line, for the debug panel. */
  raw: string;
}

export interface ParsedManifest {
  mediaSequence: number;
  /** True when the input was a master/multivariant playlist (has variants). */
  isMaster: boolean;
  cues: Cue[];
}

/** Pull a duration (seconds) out of a CUE-OUT tag in either common form:
 *  `#EXT-X-CUE-OUT:30`, `#EXT-X-CUE-OUT:DURATION=30`, `#EXT-X-CUE-OUT 30.5`. */
function parseCueOutDuration(tag: string): number | undefined {
  const withKey = tag.match(/DURATION\s*=\s*(\d+(?:\.\d+)?)/i);
  if (withKey) return Number(withKey[1]);
  const bare = tag.match(/CUE-OUT[:\s]+(\d+(?:\.\d+)?)/i);
  if (bare) return Number(bare[1]);
  return undefined;
}

/** A stable de-dup key for a cue across sliding-window polls. */
export function cueKey(c: Cue): string {
  return `${c.kind}@${c.segmentIndex}`;
}

export function parseManifestCues(text: string): ParsedManifest {
  const isMaster = /#EXT-X-STREAM-INF/.test(text);
  const seqMatch = text.match(/#EXT-X-MEDIA-SEQUENCE:(\d+)/);
  const mediaSequence = seqMatch ? Number(seqMatch[1]) : 0;

  const cues: Cue[] = [];
  if (isMaster) return { mediaSequence, isMaster, cues };

  let segIdx = mediaSequence;
  for (const line of text.split(/\r?\n/)) {
    const t = line.trim();
    if (!t.startsWith('#')) {
      // A media-segment URI line: nothing to do (EXTINF below moved the index).
      continue;
    }
    if (t.startsWith('#EXT-X-CUE-OUT-CONT')) {
      // Continuation marker — the break is already open; not a fresh trigger.
      continue;
    }
    if (t.startsWith('#EXT-X-CUE-OUT')) {
      cues.push({ kind: 'CUE-OUT', duration: parseCueOutDuration(t), segmentIndex: segIdx, raw: t });
    } else if (t.startsWith('#EXT-X-CUE-IN')) {
      cues.push({ kind: 'CUE-IN', segmentIndex: segIdx, raw: t });
    } else if (t.startsWith('#EXT-OATCLS-SCTE35') || t.startsWith('#EXT-X-SCTE35')) {
      cues.push({ kind: 'SCTE35', segmentIndex: segIdx, raw: t });
    } else if (t.startsWith('#EXTINF')) {
      segIdx += 1;
    }
  }
  return { mediaSequence, isMaster, cues };
}

/** Resolve the first variant URI from a master playlist, against `baseUrl`. */
export function firstVariantUrl(masterText: string, baseUrl: string): string | null {
  const lines = masterText.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].trim().startsWith('#EXT-X-STREAM-INF')) {
      // The variant URI is the next non-comment, non-empty line.
      for (let j = i + 1; j < lines.length; j++) {
        const uri = lines[j].trim();
        if (uri && !uri.startsWith('#')) {
          try {
            return new URL(uri, baseUrl).toString();
          } catch {
            return null;
          }
        }
      }
    }
  }
  return null;
}
